<template>
  <v-card>
    <v-dialog v-model="uploadDialog" width="500" persistent>
      <v-card>
        <v-card-title
          class="grey lighten-2"
          primary-title
        >
          {{ $t('components.CreateReferenceModel.uploading') }} {{ !!file ? file.name : '' }}
        </v-card-title>

        <v-card-text v-if="!!file && !! uploadFileProgress">
          <v-progress-linear v-model="uploadFileProgress.percent" height="25" reactive class="mt-5">
            <strong>{{ uploadFileProgress.percent }}% ({{ `${fileSizeToString(uploadFileProgress.size)}/${fileSizeToString(file.size)}` }})</strong>
          </v-progress-linear>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="uploadFileProgress.task.cancel(); uploadDialog = false"
          >
            {{ $t('components.CreateReferenceModel.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-toolbar dark color="primary">
      <v-btn icon dark @click="closeDialog">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ !!editing ? $t('components.CreateReferenceModel.editTitle') : $t('components.CreateReferenceModel.newTitle') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn dark text @click="triggerEdit" v-if="editing">{{ $t('components.CreateReferenceModel.save') }}</v-btn>
        <v-btn dark text @click="triggerCreate" v-else>{{ $t('components.CreateReferenceModel.create') }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-form ref="form" v-model="validForm">
      <v-card-text>
        <v-container>
          <v-row>
            <v-col cols="12" sm="12" md="12">
              <v-text-field v-model="codename" :rules="rules.codename" :label="$t('components.CreateReferenceModel.modelCodename')" required @keydown.space.prevent :disabled="this.editing != null"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="12" md="12">
              <v-textarea v-model="json" outlined :rules="rules.json" :label="$t('components.CreateReferenceModel.json')" required auto-grow rows="14" />
            </v-col>
          </v-row>
          <v-row v-if="editing">
            <v-col>
              <a :href="pklUrl">{{ $t('components.CreateReferenceModel.downloadPkl') }}</a>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <input v-show="false" type="file" accept=".pkl" @change="previewFile" id="file" ref="file" />
              <v-btn color="primary" dark small @click="selectFile">
                {{ $t('components.CreateReferenceModel.choosePklFile') }}
              </v-btn>
              <span class="ml-5" v-if="file">{{ !!file ? file.name : '' }} ({{ fileSizeToString(file.size) }})</span>
              <br>
              <div v-if="!editing && !this.file" style="color: #ff5252; margin-top: 5px; margin-left: 2px;">{{ $t('components.CreateReferenceModel.pklRequired') }}</div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
    </v-form>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import numbro from 'numbro'

export default {
  components: {},
  data () {
    return {
      validForm: true,
      json: null,
      codename: null,
      file: null,
      uploadDialog: false,
      pklUrl: null
    }
  },
  methods: {
    ...mapActions('admin', [
      'createReferenceModel',
      'editReferenceModel'
    ]),
    ...mapActions('storage', [
      'uploadFile',
      'resetUploadFileProgress',
      'getFileDownloadUrl'
    ]),
    closeDialog () {
      this.$emit('close-dialog')
    },
    validateForm () {
      return this.$refs.form.validate()
    },
    triggerCreate () {
      if (this.validateForm() && this.file) {
        this.triggerUploadFile().then(() => {
          this.createReferenceModel({
            createdBy: this.createdBy,
            json: this.json,
            codename: this.codename,
            pkl: this.file
          })
          this.closeDialog()
        }).catch(error => {
          console.log(error)
        })
      }
    },
    triggerEdit () {
      if (this.validateForm()) {
        if (this.file) {
          // If a new file is given, upload it and then update the DB
          this.triggerUploadFile().then(() => {
            this.editReferenceModel({
              editedBy: this.createdBy,
              json: this.json,
              codename: this.codename,
              pkl: this.file
            })
            this.closeDialog()
          }).catch(error => {
            console.log(error)
          })
        } else {
          // If no new file is given, only update the DB
          this.editReferenceModel({
            editedBy: this.createdBy,
            json: this.json,
            codename: this.codename,
            pkl: this.editing.pkl
          })
          this.closeDialog()
        }
      }
    },
    selectFile: function () {
      this.$refs.file.click()
    },
    previewFile: function () {
      this.file = this.$refs.file.files[0]
    },
    triggerUploadFile: function () {
      return new Promise((resolve, reject) => {
        this.uploadDialog = true
        this.uploadFile({
          file: this.file,
          path: `models/${this.codename}.pkl`
        }).then(() => {
          this.uploadDialog = false
          resolve()
        }, error => {
          reject(error)
        })
      })
    },
    fileSizeToString: function (size) {
      return numbro(size).format({
        output: 'byte',
        base: 'binary',
        mantissa: 1
      })
    }
  },
  computed: {
    ...mapGetters('storage', [
      'uploadFileProgress'
    ]),
    validJson () {
      try {
        return (JSON.parse(this.json) && !!this.json)
      } catch (e) {
        return false
      }
    },
    rules () {
      return {
        codename: [
          v => !!v || this.$t('components.CreateReferenceModel.codenameRequired'),
          v => (!!v && !!v.match(/^[0-9a-zA-Z_-]+$/)) || this.$t('components.CreateReferenceModel.codenameInvalid'),
          v => (!!this.editing || !this.existingModels.includes(v)) || this.$t('components.CreateReferenceModel.codenameExists')
        ],
        json: [
          v => !!v || this.$t('components.CreateReferenceModel.jsonRequired'),
          v => this.validJson || this.$t('components.CreateReferenceModel.invalidJson')
        ]
      }
    }
  },
  props: {
    createdBy: {
      type: String,
      required: true
    },
    existingModels: {
      type: Array,
      required: false,
      default: function () {
        return []
      }
    },
    editing: {
      type: Object,
      required: false
    }
  },
  watch: {
    editing: {
      immediate: true,
      handler: function () {
        if (this.editing) {
          var model = this.editing
          this.codename = model.codename
          this.json = JSON.stringify(model.config)
        }
      }
    }
  },
  created: function () {
    this.resetUploadFileProgress()
    if (this.editing) {
      this.getFileDownloadUrl({
        path: `/models/${this.codename}.pkl`
      }).then(url => {
        this.pklUrl = url
      })
    }
  }
}
</script>
